type Row = {
    university: string;
    degree: string;
    field: string;
    region: string;
    city: string;
    type: string;
    cutoff: number | null;
    places: number | null;
    price: string;
    duration: string;
    year: string;
};

type IndexedRow = Row & { _degree: string; _haystack: string };

type Config = {
    perPage: number;
    defaultSort: string;
    gradeMax: number;
    /** Academic year most cut-offs refer to; other years are shown on the card. */
    currentYear: string;
    syncUrl: boolean;
    labels: Record<string, string>;
};

type State = {
    q: string;
    field: string;
    region: string;
    type: string;
    grade: number | null;
    only: boolean;
    sort: string;
};

const SELECTOR = ".js-universities-explorer";
const URL_PREFIX = "ux_";

/** Lowercase + strip accents, so "medicína" matches "Medicina". */
function normalize(value: string): string {
    return value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

/**
 * Wraps every occurrence of the search terms in <mark>, matching on the
 * normalized text but emitting the original characters.
 */
function highlight(text: string, terms: string[]): string {
    if (!terms.length) return escapeHtml(text);

    const chars = Array.from(text);
    const norm = chars.map((c) => normalize(c));
    // Bail out on characters that don't normalize 1:1 (rare ligatures etc.)
    if (norm.some((c) => c.length !== 1)) return escapeHtml(text);

    const haystack = norm.join("");
    const marked = new Array<boolean>(chars.length).fill(false);

    for (const term of terms) {
        let from = 0;
        let at: number;
        while ((at = haystack.indexOf(term, from)) !== -1) {
            for (let i = at; i < at + term.length; i++) marked[i] = true;
            from = at + term.length;
        }
    }

    let out = "";
    let open = false;
    chars.forEach((c, i) => {
        if (marked[i] && !open) { out += "<mark>"; open = true; }
        if (!marked[i] && open) { out += "</mark>"; open = false; }
        out += escapeHtml(c);
    });

    return open ? out + "</mark>" : out;
}

class UniversitiesExplorer {
    private readonly root: HTMLElement;
    private readonly config: Config;
    private rows: IndexedRow[] = [];
    private loaded = false;
    private readonly form: HTMLFormElement;
    private readonly list: HTMLOListElement;
    private readonly count: HTMLElement;
    private readonly empty: HTMLElement;
    private readonly more: HTMLButtonElement;
    private readonly clear: HTMLButtonElement;
    private readonly locale: string;
    private readonly collator: Intl.Collator;

    private results: IndexedRow[] = [];
    private shown = 0;
    private terms: string[] = [];
    private grade: number | null = null;
    private debounce = 0;

    constructor(root: HTMLElement) {
        this.root = root;
        this.config = JSON.parse(root.dataset.config ?? "{}");
        this.locale = document.documentElement.lang || "pt-BR";
        this.collator = new Intl.Collator(this.locale, { sensitivity: "base", numeric: true });

        this.form = root.querySelector("form")!;
        this.list = root.querySelector(".nodo-ux__results")!;
        this.count = root.querySelector(".nodo-ux__count")!;
        this.empty = root.querySelector(".nodo-ux__empty")!;
        this.more = root.querySelector(".nodo-ux__more")!;
        this.clear = root.querySelector(".nodo-ux__clear")!;

        this.restoreFromUrl();
        this.bind();

        this.count.textContent = this.config.labels.loading;
        this.loadRows()
            .then((rows) => {
                this.rows = rows.map((r) => ({
                    ...r,
                    _degree: normalize(r.degree),
                    _haystack: normalize([r.degree, r.university, r.city, r.region, r.field].join(" ")),
                }));
                this.loaded = true;
                this.update();
            })
            .catch(() => {
                this.count.textContent = this.config.labels.error;
            });
    }

    /**
     * Rows live in a static JSON file (data-src, cacheable on its own); the
     * inline <script> is the fallback when the server couldn't write that file.
     */
    private async loadRows(): Promise<Row[]> {
        const src = this.root.dataset.src;
        if (src) {
            const response = await fetch(src);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
        }

        const data = this.root.querySelector<HTMLScriptElement>(".nodo-ux__data");
        return data ? JSON.parse(data.textContent ?? "[]") : [];
    }

    private bind(): void {
        // Text fields update while typing (debounced); selects and checkboxes on
        // "change", which other scripts on the page can't swallow like "input".
        const isText = (e: Event) => (e.target as HTMLElement).matches('input[type="search"], input[type="number"]');
        this.form.addEventListener("input", (e) => {
            if (!isText(e)) return;
            window.clearTimeout(this.debounce);
            this.debounce = window.setTimeout(() => this.update(), 150);
        });
        this.form.addEventListener("change", (e) => {
            if (!isText(e)) this.update();
        });

        this.more.addEventListener("click", () => this.renderPage());

        this.clear.addEventListener("click", () => {
            this.form.reset();
            this.setSort(this.config.defaultSort);
            this.update();
            this.input("q")?.focus();
        });
    }

    private input(name: string): HTMLInputElement | HTMLSelectElement | null {
        return this.form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null;
    }

    private setSort(sort: string): void {
        const select = this.input("sort");
        if (select) select.value = sort;
    }

    private readState(): State {
        const value = (name: string) => (this.input(name)?.value ?? "").trim();
        const gradeRaw = value("grade").replace(",", ".");
        const grade = gradeRaw === "" ? null : Number(gradeRaw);
        const only = this.input("only") as HTMLInputElement | null;

        return {
            q: value("q"),
            field: value("field"),
            region: value("region"),
            type: value("type"),
            grade: grade !== null && Number.isFinite(grade) ? grade : null,
            only: Boolean(only?.checked),
            sort: value("sort") || this.config.defaultSort,
        };
    }

    private update(): void {
        if (!this.loaded) return;

        const state = this.readState();
        this.terms = normalize(state.q).split(/\s+/).filter(Boolean);
        this.grade = state.grade;

        this.results = this.rows.filter((r) =>
            (!state.field || r.field === state.field) &&
            (!state.region || r.region === state.region) &&
            (!state.type || r.type === state.type) &&
            (!state.only || state.grade === null || (r.cutoff !== null && r.cutoff <= state.grade)) &&
            this.terms.every((t) => r._haystack.includes(t))
        );

        this.sort(state.sort);

        const isFiltered = Boolean(state.q || state.field || state.region || state.type || state.grade !== null);
        this.clear.hidden = !isFiltered;

        const n = this.results.length;
        const { labels } = this.config;
        this.count.textContent = n === 1
            ? labels.count_one
            : labels.count.replace("%s", n.toLocaleString(this.locale));
        this.empty.hidden = n > 0;

        this.list.innerHTML = "";
        this.shown = 0;
        this.renderPage();

        if (this.config.syncUrl) this.writeUrl(state);
    }

    private sort(sort: string): void {
        const byCutoff = (dir: 1 | -1) => (a: IndexedRow, b: IndexedRow) => {
            // Rows without a cut-off always go last.
            if (a.cutoff === null) return b.cutoff === null ? 0 : 1;
            if (b.cutoff === null) return -1;
            return (a.cutoff - b.cutoff) * dir;
        };
        const byText = (key: "degree" | "university") => (a: IndexedRow, b: IndexedRow) =>
            this.collator.compare(a[key], b[key]);

        switch (sort) {
            case "cutoff_asc":
                this.results.sort(byCutoff(1));
                break;
            case "degree_az":
                this.results.sort((a, b) => byText("degree")(a, b) || byText("university")(a, b));
                break;
            case "university_az":
                this.results.sort((a, b) => byText("university")(a, b) || byText("degree")(a, b));
                break;
            case "relevance": {
                if (!this.terms.length) {
                    this.results.sort(byCutoff(-1));
                    break;
                }
                // Degree starting with the query > degree containing it > other matches.
                const q = this.terms.join(" ");
                const score = (r: IndexedRow) =>
                    r._degree.startsWith(q) ? 0 : this.terms.every((t) => r._degree.includes(t)) ? 1 : 2;
                this.results.sort((a, b) => score(a) - score(b) || byCutoff(-1)(a, b));
                break;
            }
            default:
                this.results.sort(byCutoff(-1));
        }
    }

    private renderPage(): void {
        const next = this.results.slice(this.shown, this.shown + this.config.perPage);
        this.list.insertAdjacentHTML("beforeend", next.map((r) => this.card(r)).join(""));
        this.shown += next.length;
        this.more.hidden = this.shown >= this.results.length;
    }

    private formatCutoff(value: number): string {
        return value.toLocaleString(this.locale, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    }

    /** "Nota de corte", or "Nota de corte 2025-26" when the row is from another year. */
    private cutoffLabel(r: IndexedRow): string {
        const { labels, currentYear } = this.config;
        return r.year && currentYear && r.year !== currentYear
            ? labels.cutoff_year.replace("%s", r.year)
            : labels.cutoff;
    }

    private card(r: IndexedRow): string {
        const { labels } = this.config;
        const terms = this.terms;

        const location = [r.city, r.region].filter(Boolean).join(", ");
        const tags = [r.field, r.type].filter(Boolean)
            .map((t) => `<span class="nodo-ux__tag">${escapeHtml(t)}</span>`).join("");

        const stats = [
            r.cutoff !== null ? stat(this.cutoffLabel(r), this.formatCutoff(r.cutoff), true) : "",
            r.places !== null ? stat(labels.places, r.places.toLocaleString(this.locale)) : "",
            r.price ? stat(labels.price, escapeHtml(r.price)) : "",
            r.duration ? stat(labels.duration, escapeHtml(r.duration)) : "",
        ].join("");

        let badge = "";
        if (this.grade !== null && r.cutoff !== null) {
            const gap = r.cutoff - this.grade;
            badge = gap <= 0
                ? `<p class="nodo-ux__badge nodo-ux__badge--in">${escapeHtml(labels.in)}</p>`
                : `<p class="nodo-ux__badge nodo-ux__badge--out">${escapeHtml(labels.out.replace("%s", this.formatCutoff(gap)))}</p>`;
        }

        return `
            <li class="nodo-ux__card">
                <div class="nodo-ux__body">
                    <h3 class="nodo-ux__degree">${highlight(r.degree, terms)}</h3>
                    <p class="nodo-ux__university">${highlight(r.university, terms)}${location ? ` · <span>${highlight(location, terms)}</span>` : ""}</p>
                    ${tags ? `<div class="nodo-ux__tags">${tags}</div>` : ""}
                    ${badge}
                </div>
                <dl class="nodo-ux__stats">${stats}</dl>
            </li>`;
    }

    private restoreFromUrl(): void {
        this.setSort(this.config.defaultSort);
        if (!this.config.syncUrl) return;

        const params = new URLSearchParams(window.location.search);
        for (const name of ["q", "field", "region", "type", "grade", "sort"]) {
            const value = params.get(URL_PREFIX + name);
            const el = this.input(name);
            if (value === null || !el) continue;
            // Ignore values that don't exist as <option>s anymore.
            if (el instanceof HTMLSelectElement && !Array.from(el.options).some((o) => o.value === value)) continue;
            el.value = value;
        }
        const only = this.input("only") as HTMLInputElement | null;
        if (only) only.checked = params.get(URL_PREFIX + "only") === "1";
    }

    private writeUrl(state: State): void {
        const url = new URL(window.location.href);
        const set = (key: string, value: string) =>
            value ? url.searchParams.set(URL_PREFIX + key, value) : url.searchParams.delete(URL_PREFIX + key);

        set("q", state.q);
        set("field", state.field);
        set("region", state.region);
        set("type", state.type);
        set("grade", state.grade === null ? "" : String(state.grade));
        set("only", state.only ? "1" : "");
        set("sort", state.sort === this.config.defaultSort ? "" : state.sort);

        window.history.replaceState(window.history.state, "", url);
    }
}

function stat(label: string, value: string, primary = false): string {
    return `<div class="nodo-ux__stat${primary ? " nodo-ux__stat--primary" : ""}"><dt>${escapeHtml(label)}</dt><dd>${value}</dd></div>`;
}

function init(scope: ParentNode = document): void {
    scope.querySelectorAll<HTMLElement>(SELECTOR).forEach((el) => {
        if (el.dataset.uxReady) return;
        el.dataset.uxReady = "1";
        new UniversitiesExplorer(el);
    });
}

/**
 * Inits on page load and again whenever Elementor (re)renders the widget
 * in the editor preview.
 */
export function bindUniversitiesExplorer(): void {
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => init());
    } else {
        init();
    }

    const w = window as any;
    const hook = () =>
        w.elementorFrontend?.hooks?.addAction(
            "frontend/element_ready/nodo-universities-explorer.default",
            ($scope: any) => init($scope[0] ?? $scope)
        );

    if (w.elementorFrontend?.hooks) {
        hook();
    } else if (w.jQuery) {
        w.jQuery(window).on("elementor/frontend/init", hook);
    }
}
